/**
 * Timezone-aware date math for the Reporting tab.
 *
 * Why this exists
 * ---------------
 * The original DateRangePicker used `new Date()` + `setHours(0, 0, 0, 0)` to
 * compute "today" / "yesterday" / etc. That gives the BROWSER's local
 * midnight. If you're in EDT and the account's reporting timezone is also
 * EDT, that happens to work; but on a multi-user system someone could be
 * logged into an EST-configured account from a UTC laptop, and the date
 * ranges would shift by 5 hours and start crossing UTC bucket boundaries
 * weirdly.
 *
 * Fix: do all "start of day" math in the account's IANA timezone via
 * `Intl.DateTimeFormat`. Returns are JS Date objects (i.e. true UTC
 * moments) representing those local midnights — exactly what the backend
 * expects on the wire.
 *
 * No external library used; `Intl.DateTimeFormat` is available everywhere
 * we care about, and we only need a handful of operations.
 */

/**
 * Return today's date in the given IANA timezone as a {year, month, day}.
 * month is 1-based to match how people speak about dates.
 */
export function todayInTz(tz: string): { year: number; month: number; day: number } {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
  // en-CA formats as 'YYYY-MM-DD' which is convenient.
  const [y, m, d] = fmt.format(new Date()).split('-').map(Number);
  return { year: y, month: m, day: d };
}

/**
 * Find the UTC moment that is `00:00:00.000` LOCAL to the given timezone
 * on the given Y/M/D. E.g. `localMidnightUtc(2026, 6, 2, 'America/New_York')`
 * returns the Date object for 2026-06-02 04:00:00 UTC (or 05:00 in winter).
 *
 * Implementation note: we can't construct a Date directly in another TZ in
 * vanilla JS. We make an initial UTC guess at `Y-M-D 00:00 UTC`, ask what
 * that looks like in the target TZ via Intl, measure the delta, and slide
 * the moment by that delta. Two iterations handle DST transitions (the
 * second pass catches the offset shift when the first guess straddles a
 * spring-forward/fall-back boundary).
 */
export function localMidnightUtc(year: number, month: number, day: number, tz: string): Date {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });

  // First guess: the desired local time interpreted as if it were UTC.
  let guess = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));

  for (let i = 0; i < 2; i++) {
    const parts = fmt.formatToParts(guess);
    const get = (type: string) => parseInt(parts.find(p => p.type === type)!.value, 10);
    // Intl's "hour: '2-digit', hour12: false" emits 24 as '24' at midnight in
    // some Node builds. Normalize.
    const h = get('hour') === 24 ? 0 : get('hour');
    const seenAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), h, get('minute'), get('second'));
    const desiredAsUtc = Date.UTC(year, month - 1, day, 0, 0, 0);
    const diffMs = desiredAsUtc - seenAsUtc;
    if (Math.abs(diffMs) < 1000) {break;}
    guess = new Date(guess.getTime() + diffMs);
  }

  return guess;
}

/**
 * Shift a {year, month, day} by N calendar days. Handles month/year wrap
 * by going through JS Date, which knows how to handle Feb 29 etc.
 */
export function addDays(
  ymd: { year: number; month: number; day: number },
  days: number
): { year: number; month: number; day: number } {
  const d = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day));
  d.setUTCDate(d.getUTCDate() + days);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/**
 * Start-of-month for the month containing the given date, in the given tz.
 */
export function startOfMonth(
  ymd: { year: number; month: number; day: number }
): { year: number; month: number; day: number } {
  return { year: ymd.year, month: ymd.month, day: 1 };
}
